<template>
  <work-topic-skeleton v-if="!currentWork && !isPageLoaded"></work-topic-skeleton>
  <v-content v-else id="workContent">
    <app-loading-overlay></app-loading-overlay>
    <v-container class="color1 pageHeader">
      <v-row>
        <v-col cols="12">
          <div class="text-center align-end justify-end">
            <h1 class="display-1">{{ $t("myWork") }}</h1>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-card tile outlined>
        <v-container class="workContainer">
          <v-row>
            <v-col cols="12">
              <div class="text-center">
                <h1>{{ currentWork.title }}</h1>
                <h2 v-if="currentWork.subtitle !== ''">{{ currentWork.subtitle }}</h2>
                <h3>
                  {{ $t("author") }}:
                  <i>{{ currentWork.author }}</i>
                </h3>
                <p v-if="currentWork.email">
                  <v-icon left>mdi-email</v-icon>
                  {{ currentWork.email }}
                </p>
                <p>{{ currentWork.date }}</p>
              </div>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row v-if="currentWork.image">
            <v-container class="contentPreview">
              <v-row>
                <v-img :src="currentWork.image" max-height="500" contain></v-img>
              </v-row>
            </v-container>
          </v-row>
          <v-row v-if="currentWork.preview">
            <v-container class="contentPreview">
              <v-row v-for="(preview, i) in currentWork.preview" :key="i">
                <v-col cols="12">
                  <v-row>
                    <h3>{{ preview.heading }}</h3>
                  </v-row>
                  <br />
                  <v-row v-for="(content, j) in preview.content" :key="j">
                    <p v-if="content.type==='paragraph'">{{ content.text }}</p>
                    <ul v-else-if="content.type='list'">
                      <li v-for="(item, k) in content.points" :key="k">{{ item.text }}</li>
                      <br />
                    </ul>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-row>
          <v-divider></v-divider>
          <v-row>
            <v-col cols="12">
              <div class="text-left">
                <p v-if="currentWork.description">
                  <b>{{ $t("description") }}: </b>
                  <span v-html="currentWork.description"></span>
                </p>
                <p v-if="currentWork.publisher">
                  <b>{{ $t("publisher") }}: </b>
                  <span v-html="currentWork.publisher"></span>
                </p>
                <p v-if="currentWork.isbn">
                  <b>ISBN: </b>
                  <span>{{ currentWork.isbn }}</span>
                </p>
                <p v-if="currentWork.myContribution">
                  <b>{{ $t("myContribution") }}: </b>
                  <span>{{ currentWork.myContribution }}</span>
                </p>
                <p v-if="currentWork.targetGroup">
                  <b>{{ $t("targetGroup") }}: </b>
                  <span>{{ currentWork.targetGroup }}</span>
                </p>
                <p v-if="currentWork.keywords">
                  <b>{{ $t("keywords") }}: </b>
                  <span>{{ currentWork.keywords }}</span>
                </p>
                <p v-if="currentWork.external">
                  <span><b>{{ $t("externalSource") }} :</b></span>
                  <a href="#" @click.prevent="externalLinkClick(currentWork.external)">{{ currentWork.external }}</a>
                </p>
                <p v-if="currentWork.doi">
                  <span><b>DOI: </b></span>
                  <a href="#" @click.prevent="externalLinkClick(currentWork.doi)">{{ currentWork.doi }}</a>
                </p>
              </div>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row>
            <v-col cols="12">
              <v-row>
                <v-col cols="2">
                  <v-img :src="pdfImage" max-height="50" contain transition="fab-transition"></v-img>
                </v-col>
                <v-col cols="auto">
                  <v-dialog v-model="downloadForm.open" max-width="600">
                    <template #activator="{on: dialog}">
                      <v-btn
                        v-on="dialog"
                        color="success"
                        large
                        :disabled="currentWork.link === ''"
                      >{{ $t("downloadFullText") }}</v-btn>
                    </template>
                    <v-card>
                      <v-form v-model="downloadForm.isValid" ref="downloadForm">
                        <v-card-title class="color5 background--text">{{ $t("downloadFile") }}</v-card-title>
                        <v-container>
                          <v-row class="formRow">
                            <div>
                              <p>
                                {{ $t("workName") }}:
                                <strong>
                                  {{ currentWork.title }}
                                  <span
                                    v-if="currentWork.subtitle"
                                  >: {{currentWork.subtitle}}</span>
                                </strong>
                              </p>
                            </div>
                          </v-row>
                          <v-row class="formRow">
                            <div>
                              <p>
                                {{ $t("author") }}:
                                <strong>{{ currentWork.author }}</strong>
                              </p>
                            </div>
                          </v-row>
                          <v-row class="formRow">
                            <div>
                              <p>{{ $t("downloadWorkInstruction") }}</p>
                            </div>
                          </v-row>
                          <v-row class="formRow">
                            <v-col cols="6" class="contactCol">
                              <v-text-field
                                outlined
                                name="firstName"
                                :label="$t('firstName')"
                                v-model="downloadForm.data.firstName"
                                :rules="[downloadForm.rules.required]"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6" class="contactCol">
                              <v-text-field
                                outlined
                                name="lastName"
                                :label="$t('lastName')"
                                v-model="downloadForm.data.lastName"
                                :rules="[downloadForm.rules.required]"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                          <v-row class="formRow">
                            <v-col class="contactCol" cols="12">
                              <v-text-field
                                outlined
                                name="email"
                                :label="$t('email')"
                                v-model="downloadForm.data.email"
                                :rules="[downloadForm.rules.email]"
                                data-qev-email-field
                              ></v-text-field>
                            </v-col>
                          </v-row>
                          <v-row class="formRow">
                            <v-col class="contactCol" cols="12">
                              <v-checkbox
                                v-model="downloadForm.data.dataConsent"
                                :label="$t('dataProtectionLabel')"
                              >
                                <template v-slot:label>
                                  <div>
                                    {{ $t('dataProtectionLabel') }}
                                    <span
                                      class="linkSpan"
                                      @click="gotoRoute('privacyPolicy')"
                                    >Privacy Policy</span> and
                                    <span
                                      class="linkSpan"
                                      @click="gotoRoute('termsAndConditions')"
                                    >Terms & Conditions</span>
                                  </div>
                                </template>
                              </v-checkbox>
                            </v-col>
                          </v-row>
                          <v-row class="formRow">
                            <span class="grey--text">
                              <i>{{ $t("requiredField") }}</i>
                            </span>
                          </v-row>
                        </v-container>
                        <v-divider></v-divider>
                        <v-card-actions>
                          <div style="margin: 1.5%;">
                            <v-btn color="success" ma-5 :disabled="!downloadForm.isValid || !downloadForm.data.dataConsent" @click="downloadClick()">
                              {{ $t("download") }}
                            </v-btn>
                          </div>
                        </v-card-actions>
                      </v-form>
                    </v-card>
                  </v-dialog>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-container>
  </v-content>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import WorkTopicSkeleton from "@/components/myWork/WorkTopicSkeleton";

export default {
  name: "WorkTopic",
  data() {
    return {
      isPageLoaded: false,
      currentWork: undefined,
      pdfImage:
        "https://firebasestorage.googleapis.com/v0/b/newecohelp.appspot.com/o/myWork%2Fpdf.svg?alt=media&token=140b0ed4-4e31-48d0-a41d-9881398ef87f",
      downloadForm: {
        open: false,
        isValid: false,
        data: {
          firstName: "",
          lastName: "",
          email: "",
          dataConsent: false,
        },
        rules: {
          required: (value) => !!value || "Required",
          email: (value) =>
            (!!value && this.validEmail(value)) || "Invalid e-mail",
        },
      },
    };
  },
  components: {
    workTopicSkeleton: WorkTopicSkeleton,
  },
  computed: {
    ...mapGetters(["myWorkLoading", "getMyWork"]),
  },
  methods: {
    ...mapActions(["fetchMyWorkFromDatabase", "handleDownloadMyWork"]),
    waitForLoading() {
      const prom = (resolve) => {
        if (!this.getMyWork) {
          setTimeout(() => prom(resolve), 100);
        } else {
          resolve();
        }
      };
      return new Promise(prom);
    },
    downloadClick() {
      this.$refs.downloadForm.validate();
      const workDownloadInfo = {
        requester: {
          firstName: this.downloadForm.data.firstName,
          lastName: this.downloadForm.data.lastName,
          email: this.downloadForm.data.email,
          dataConsent: this.downloadForm.data.dataConsent
        },
        work: {
          title: this.currentWork.title,
          id: this.currentWork.id,
          link: this.currentWork.link
        }
      }
      
      this.handleDownloadMyWork(workDownloadInfo);
      
      this.logEvent("button_click", {
        name: "workDownload",
        workName: this.currentWork.title,
        workId: this.currentWork.id
      });
      this.downloadForm.open = false;
      this.$refs.downloadForm.reset()
    },
    externalLinkClick(extLink) {
      window.open(extLink, "_blank");
      this.logEvent("link_click", {
        name: "workExternalLinkClick",
        workTitle: this.currentWork.title,
        link: extLink,
      });
    },
  },
  beforeMount() {
    if (!this.getMyWork) {
      this.fetchMyWorkFromDatabase();
    }
    this.waitForLoading().then(() => {
      const topicId = this.$route.params.id;
      let workFound = false;
      for (let i = 0; i < this.getMyWork.length; i++) {
        if (this.getMyWork[i].works) {
          for (let j = 0; j < this.getMyWork[i].works.length; j++) {
            if (topicId === this.getMyWork[i].works[j].id) {
              workFound = true;
              this.currentWork = this.getMyWork[i].works[j];
              break;
            }
          }
        }
        if (workFound) {
          break;
        }
      }
      if (!workFound) {
        this.$router.push({ name: "home" });
      }
      this.isPageLoaded = true;
    });
  },
  mounted() {
    this.logEvent("page_load", {
      pageName: "workTopic",
      lang: this.$i18n.locale,
    });
  },
  metaInfo() {
    return {
      title: !this.isPageLoaded ? "Page is loading" : `${this.currentWork.title}: ${this.currentWork.subtitle} - Dr. Rashmee Rajkarnikar`,
      meta: [
        {
          name: "description",
          content: !this.isPageLoaded ? "Page is loading" : this.currentWork.description ? `${this.currentWork.description}` : `${this.currentWork.title}`
        },
        {
          property: "og:title",
          content: !this.isPageLoaded ? "Page is loading" : `Work of ${this.currentWork.author}`
        },
        {
          property: "og:site_name",
          content: !this.isPageLoaded ? "Page is loading" : `${this.currentWork.title}: ${this.currentWork.subtitle}`
        },
        {
          property: "og:description",
          content: !this.isPageLoaded ? "Page is loading" : this.currentWork.preview ? `${this.currentWork.preview[0].content[0].text}` : `${this.currentWork.title}`
        },
        {
          property: "og:type",
          content: "article"
        },
        {
          property: "og:url",
          content: `https://www.dr-rashmee.com + ${this.$route.path}`
        },
        {
          property: "og:image",
          content: !this.isPageLoaded ? "" : `${this.currentWork.image}`
        }
      ]
    }
  }
};
</script>

<style scoped>
#workContent {
  padding: 0 !important;
}
.workContainer {
  padding-left: 5%;
  padding-right: 5%;
}
.contentPreview {
  padding-left: 10%;
  padding-right: 10%;
}
.formRow {
  margin-left: 5%;
  margin-right: 5%;
}
.contactCol {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
</style>